import { Col, Row } from "antd";
import cuid from "cuid";
import { get, isEmpty, set } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineCallReceived,
  MdOutlinePinDrop,
  MdOutlineQrCode,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";
import {
  CONTACT_POINT_SYSTEM,
  PAYMENT_RECEIVER,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Refund = ({
  formProps,
  setDisabled,
  values,
  setPercent,
  errors,
  initialValues,
  setFieldValue,
  isFieldRequired,
}) => {
  const [cities, setCities] = useState([]);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const [addressZipCodeCity] = useLazyQuery(FRANCE_ADDRESS_ZIP_CODE_CITY);

  useEffect(() => {
    addressZipCodeCity({
      variables: {
        where: {
          zipCode: get(
            initialValues,
            `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          ),
        },
      },
      onCompleted: ({ franceAddressZipCodeCity: { response } }) => {
        setCities(response);
      },
    });
  }, []);

  const PAYMENT_RECEIVER_VALUES = {
    [PAYMENT_RECEIVER.SAME_ACCOUNT]: () => {
      // Copy payer data to receiver
      set(
        initialValues,
        "payment.receiver",
        get(initialValues, "payment.payer"),
      );
      // Copy payer telecoms to receiver telecoms
      set(
        initialValues,
        "telecoms.receiver",
        get(initialValues, "telecoms.payer"),
      );
    },
    [PAYMENT_RECEIVER.ANOTHER_ACCOUNT]: () => {
      set(initialValues, "payment.receiver", {
        id: cuid(),
        owner: { id: cuid() },
      });
      // Initialize telecoms.receiver if it doesn't exist
      if (!get(initialValues, "telecoms.receiver")) {
        set(initialValues, "telecoms.receiver", {});
      }
    },
  };

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Input
              type="radio"
              variant="group"
              icon={<MdOutlineCallReceived size={16} />}
              label={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT.LABEL
              }
              value={get(initialValues, "isPayerReceiver")}
              values={[
                {
                  value: PAYMENT_RECEIVER.SAME_ACCOUNT,
                  label:
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .OPTIONS.USER_ACCOUNT,
                  popover:
                    !(
                      get(initialValues, "payment.payer.IBAN") &&
                      get(initialValues, "payment.payer.BIC")
                    ) &&
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .POPOVER,
                },
                {
                  value: PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
                  label:
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .OPTIONS.OTHER,
                },
              ]}
              errors={errors["isPayerReceiver"]}
              touched={!isEmpty(errors["isPayerReceiver"])}
              onBlur={() => setPercent(initialValues)}
              disabled={
                !(
                  get(initialValues, "payment.payer.IBAN") &&
                  get(initialValues, "payment.payer.BIC")
                )
              }
              onChange={(value) => {
                set(initialValues, "isPayerReceiver", value);
                const isSameAccount = value === PAYMENT_RECEIVER.SAME_ACCOUNT;
                setDisabled((prev) => ({
                  ...prev,
                  proof: {
                    ...prev.proof,
                    refundAccount: isSameAccount,
                  },
                  // Disable receiver payment fields when same account
                  payment: {
                    ...prev.payment,
                    receiver: {
                      ...prev.payment?.receiver,
                      owner: {
                        firstName: isSameAccount,
                        lastName: isSameAccount,
                      },
                      IBAN: isSameAccount,
                      BIC: isSameAccount,
                    },
                  },
                  // Disable receiver telecoms when same account
                  telecoms: {
                    ...prev.telecoms,
                    receiver: {
                      [CONTACT_POINT_SYSTEM.ADDRESS]: {
                        value: isSameAccount,
                      },
                      [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
                        value: isSameAccount,
                      },
                      [CONTACT_POINT_SYSTEM.CITY]: {
                        value: isSameAccount,
                      },
                    },
                  },
                }));
                PAYMENT_RECEIVER_VALUES[value]();
              }}
              required={isFieldRequired("isPayerReceiver")}
            />
          </Col>
          {get(initialValues, "isPayerReceiver") ===
            PAYMENT_RECEIVER.ANOTHER_ACCOUNT && (
            <>
              <Col lg={12} xs={12}>
                <Input
                  label={SUBSCRIPTION.FORM.OWNER.LASTNAME.LABEL}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.OWNER.LASTNAME.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    "payment.receiver.owner.lastName",
                  )}
                  errors={errors["payment.receiver.owner.lastName"]}
                  touched={!isEmpty(errors["payment.receiver.owner.lastName"])}
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "payment.receiver.owner.lastName",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.owner.lastName")}
                />
              </Col>
              <Col lg={12} xs={12}>
                <Input
                  label={SUBSCRIPTION.FORM.OWNER.FIRSTNAME.LABEL}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.OWNER.FIRSTNAME.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    "payment.receiver.owner.firstName",
                  )}
                  errors={errors["payment.receiver.owner.firstName"]}
                  touched={!isEmpty(errors["payment.receiver.owner.firstName"])}
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "payment.receiver.owner.firstName",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.owner.firstName")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.IBAN.LABEL}
                  icon={<MdOutlineQrCode size={16} />}
                  placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                  defaultValue={get(initialValues, "payment.receiver.IBAN")}
                  errors={errors["payment.receiver.IBAN"]}
                  touched={!isEmpty(errors["payment.receiver.IBAN"])}
                  onChange={({ target: { value } }) => {
                    set(initialValues, "payment.receiver.IBAN", value);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.IBAN")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.BIC.LABEL}
                  icon={<MdOutlineQrCode size={16} />}
                  placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                  defaultValue={get(initialValues, "payment.receiver.BIC")}
                  errors={errors["payment.receiver.BIC"]}
                  touched={!isEmpty(errors["payment.receiver.BIC"])}
                  onChange={({ target: { value } }) => {
                    set(initialValues, "payment.receiver.BIC", value);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.BIC")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.ADDRESS.LABEL}
                  icon={<MdOutlineAddRoad size={16} />}
                  placeholder={SUBSCRIPTION.FORM.ADDRESS.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                  )}
                  errors={
                    errors[
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`
                      ],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                  )}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.COMPLEMENT.LABEL}
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  icon={<MdOutlineAddRoad size={16} />}
                  placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                  )}
                  errors={
                    errors[
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`
                      ],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                  )}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.POSTAL_CODE.LABEL}
                  icon={<MdOutlinePinDrop size={16} />}
                  placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  )}
                  errors={
                    errors[
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`
                      ],
                    )
                  }
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      value,
                    );
                    // Clear city when zip code changes
                    set(
                      initialValues,
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      undefined,
                    );
                    // Trigger city fetch when zip code changes
                    if (value) {
                      addressZipCodeCity({
                        variables: {
                          where: {
                            zipCode: value,
                          },
                        },
                        onCompleted: ({
                          franceAddressZipCodeCity: { response },
                        }) => {
                          setCities(response);
                        },
                      });
                    } else {
                      setCities([]);
                    }
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  )}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Select
                  icon={<MdOutlineSouthAmerica size={16} />}
                  label={SUBSCRIPTION.FORM.CITY.LABEL}
                  placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                  showSearch
                  options={cities}
                  defaultValue={get(
                    initialValues,
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  )}
                  errors={
                    errors[
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`
                      ],
                    )
                  }
                  onChange={(value) => {
                    set(
                      initialValues,
                      `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired(
                    `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  )}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Refund;
